import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { auth, db } from '@/lib/firebase';
import { toast } from 'sonner';
import {
    collection,
    doc,
    setDoc,
    updateDoc,
    query,
    where,
    getDocs,
    Timestamp,
    FirestoreDataConverter,
    QueryDocumentSnapshot,
    SnapshotOptions,
    serverTimestamp,
    FieldValue
} from 'firebase/firestore';
import { useAccount } from './AccountContext';
import { useBudgetManager } from './BudgetManagerContext';

// Types
interface BankConnection {
    id?: string;
    userId: string;
    createdBy: string;
    budgetId: string;
    finicityCustomerId: string;
    accessToken: string;
    lastSyncTime: Date;
    status: 'active' | 'disconnected' | 'error';
    error?: string;
    institutionId?: string;
    institutionName?: string;
    createdAt: Date | FieldValue;
    updatedAt: Date | FieldValue;
}

interface BankLinkingState {
    isLinking: boolean;
    error: string | null;
    lastSyncTime: Date | null;
    activeConnections: BankConnection[];
    loading: boolean;
}

type BankLinkingAction =
    | { type: 'SET_LINKING'; payload: boolean }
    | { type: 'SET_ERROR'; payload: string | null }
    | { type: 'SET_LAST_SYNC'; payload: Date }
    | { type: 'SET_CONNECTIONS'; payload: BankConnection[] }
    | { type: 'ADD_CONNECTION'; payload: BankConnection }
    | { type: 'UPDATE_CONNECTION'; payload: BankConnection }
    | { type: 'SET_LOADING'; payload: boolean };

interface BankLinkingContextType {
    startLinking: () => Promise<void>;
    getLinkToken: () => Promise<string>;
    handleBankCallback: (publicToken: string) => Promise<void>;
    refreshAccounts: () => Promise<void>;
    disconnectBank: (connectionId: string) => Promise<void>;
    isLinking: boolean;
    error: string | null;
    lastSyncTime: Date | null;
    activeConnections: BankConnection[];
    loading: boolean;
}

const initialState: BankLinkingState = {
    isLinking: false,
    error: null,
    lastSyncTime: null,
    activeConnections: [],
    loading: false,
};

const bankConnectionConverter: FirestoreDataConverter<BankConnection> = {
    toFirestore: (bankConnection: BankConnection) => ({
        ...bankConnection,
        lastSyncTime: Timestamp.fromDate(bankConnection.lastSyncTime),
        createdAt: bankConnection.createdAt instanceof Date ? Timestamp.fromDate(bankConnection.createdAt) : bankConnection.createdAt,
        updatedAt: bankConnection.updatedAt instanceof Date ? Timestamp.fromDate(bankConnection.updatedAt) : bankConnection.updatedAt,
    }),
    fromFirestore: (
        snapshot: QueryDocumentSnapshot,
        options: SnapshotOptions
    ): BankConnection => {
        const data = snapshot.data(options);
        return {
            ...data,
            id: snapshot.id,
            lastSyncTime: data.lastSyncTime.toDate(),
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
        } as BankConnection;
    },
};

const BankLinkingContext = createContext<BankLinkingContextType | undefined>(undefined);

export function BankLinkingProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(bankLinkingReducer, initialState);
    const { addAccount } = useAccount();
    const { state: budgetState } = useBudgetManager();

    function bankLinkingReducer(state: BankLinkingState, action: BankLinkingAction): BankLinkingState {
        switch (action.type) {
            case 'SET_LINKING':
                return { ...state, isLinking: action.payload };
            case 'SET_ERROR':
                return { ...state, error: action.payload };
            case 'SET_LAST_SYNC':
                return { ...state, lastSyncTime: action.payload };
            case 'SET_CONNECTIONS':
                return { ...state, activeConnections: action.payload };
            case 'ADD_CONNECTION':
                return { ...state, activeConnections: [...state.activeConnections, action.payload] };
            case 'UPDATE_CONNECTION':
                return {
                    ...state,
                    activeConnections: state.activeConnections.map(conn =>
                        conn.id === action.payload.id ? action.payload : conn
                    ),
                };
            case 'SET_LOADING':
                return { ...state, loading: action.payload };
            default:
                return state;
        }
    }

    const loadActiveConnections = useCallback(async () => {
        if (!auth.currentUser?.uid || !budgetState.currentBudget?.id) return;

        try {
            const connectionsRef = collection(db, 'bank_connections').withConverter(bankConnectionConverter);
            const connectionsQuery = query(
                connectionsRef,
                where('budgetId', '==', budgetState.currentBudget.id),
                where('userId', '==', auth.currentUser.uid),
                where('status', '==', 'active')
            );

            const snapshot = await getDocs(connectionsQuery);
            const connections = snapshot.docs.map(doc => doc.data());
            dispatch({ type: 'SET_CONNECTIONS', payload: connections });
        } catch (error) {
            console.error('Error loading connections:', error);
            toast.error('Failed to load bank connections');
        }
    }, [budgetState.currentBudget?.id]);

    React.useEffect(() => {
        loadActiveConnections();
    }, [loadActiveConnections]);

    const createFinicityCustomer = async (token: string, appKey: string) => {
        const response = await fetch('/api/connect/customer', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Finicity-App-Key': appKey,
                'Finicity-App-Token': token
            }
        });

        if (!response.ok) {
            throw new Error('Failed to create Finicity customer');
        }

        const data = await response.json();
        return data.id;
    };

    const saveBankConnection = async (connection: Omit<BankConnection, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'budgetId'>) => {
        if (!auth.currentUser?.uid || !budgetState.currentBudget?.id) {
            throw new Error('User not authenticated or no budget selected');
        }

        const connectionsRef = collection(db, 'bank_connections').withConverter(bankConnectionConverter);
        const newConnection: Omit<BankConnection, 'id'> = {
            ...connection,
            createdBy: auth.currentUser.uid,
            budgetId: budgetState.currentBudget.id,
            lastSyncTime: new Date(),
            createdAt: serverTimestamp(), // Use server timestamp
            updatedAt: serverTimestamp(), // Use server timestamp
        };

        console.log('Saving bank connection:', newConnection);

        const docRef = doc(connectionsRef);
        await setDoc(docRef, newConnection);
        const savedConnection = { ...newConnection, id: docRef.id };
        dispatch({ type: 'ADD_CONNECTION', payload: savedConnection });
        return savedConnection;
    };

    const updateBankConnection = async (connection: BankConnection) => {
        if (!connection.id) throw new Error('Connection ID is required');
        const connectionRef = doc(db, 'bank_connections', connection.id).withConverter(bankConnectionConverter);
        await updateDoc(connectionRef, {
            ...connection,
            updatedAt: new Date(),
        });
    };

    const getLinkToken = useCallback(async () => {
        if (!auth.currentUser?.uid) {
            throw new Error('User must be logged in');
        }

        if (!budgetState.currentBudget?.id) {
            throw new Error('No active budget selected');
        }

        const appKey = process.env.NEXT_PUBLIC_MASTERCARD_APP_KEY;
        const partnerId = process.env.NEXT_PUBLIC_MASTERCARD_PARTNER_ID;
        const partnerSecret = process.env.NEXT_PUBLIC_MASTERCARD_PARTNER_SECRET;

        if (!appKey || !partnerId || !partnerSecret) {
            throw new Error('Missing configuration');
        }

        try {
            const tokenResponse = await fetch('/api/auth/mastercard-token', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ partnerId, partnerSecret })
            });

            if (!tokenResponse.ok) throw new Error('Failed to get access token');

            const { token } = await tokenResponse.json();
            
            const customerId = await createFinicityCustomer(token, appKey);
            
            const response = await fetch('/api/connect/token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'finicity-app-token': token,
                    'finicity-app-key': appKey
                },
                body: JSON.stringify({ partnerId, customerId })
            });

            if (!response.ok) throw new Error('Failed to get connect token');

            const { link } = await response.json();

            // Save initial connection record
            await saveBankConnection({
                userId: auth.currentUser.uid,
                finicityCustomerId: customerId,
                accessToken: token,
                lastSyncTime: new Date(),
                status: 'active'
            });

            return link;
        } catch (error) {
            console.error('Error getting link token:', error);
            toast.error('Failed to initialize bank connection');
            throw error;
        }
    }, [budgetState.currentBudget?.id]);

    const handleBankCallback = useCallback(async (publicToken: string) => {
        if (!auth.currentUser || !budgetState.currentBudget?.id) {
            throw new Error('User not authenticated or no budget selected');
        }

        try {
            const connection = state.activeConnections[state.activeConnections.length - 1];
            if (!connection) throw new Error('No active connection found');

            const response = await fetch('/api/connect/exchange', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'finicity-app-token': connection.accessToken,
                    'finicity-app-key': process.env.NEXT_PUBLIC_MASTERCARD_APP_KEY!
                },
                body: JSON.stringify({
                    customerId: connection.finicityCustomerId,
                    institutionLoginId: publicToken
                })
            });

            if (!response.ok) throw new Error('Failed to exchange token');

            const data = await response.json();

            // Update connection with institution info
            await updateBankConnection({
                ...connection,
                institutionId: data.accounts[0]?.institutionId,
                institutionName: data.accounts[0]?.institutionName,
                updatedAt: new Date()
            });

            // Add accounts to Firestore
            for (const account of data.accounts) {
                await addAccount({
                    name: account.name,
                    type: account.type,
                    balance: account.balance,
                    availableToBudget: account.detail?.availableBalance || 0,
                    color: account.institutionId ?
                        `#${(parseInt(account.institutionId) * 123456).toString(16).slice(0, 6)}` :
                        '#' + Math.floor(Math.random() * 16777215).toString(16),
                    accessToken: connection.accessToken,
                    externalId: account.id,
                    institution: account.institutionId,
                    finicityCustomerId: connection.finicityCustomerId,
                    createdBy: auth.currentUser.uid
                });
            }

            toast.success('Bank accounts connected successfully');
        } catch (error) {
            console.error('Error in bank callback:', error);
            toast.error('Failed to complete bank connection');
            throw error;
        }
    }, [state.activeConnections, addAccount, budgetState.currentBudget?.id]);

    const refreshAccounts = useCallback(async () => {
        if (!auth.currentUser || !budgetState.currentBudget?.id) {
            throw new Error('User not authenticated or no budget selected');
        }

        dispatch({ type: 'SET_LOADING', payload: true });

        try {
            const promises = state.activeConnections.map(async (connection) => {
                const response = await fetch('/api/connect/refresh', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'finicity-app-token': connection.accessToken,
                        'finicity-app-key': process.env.NEXT_PUBLIC_MASTERCARD_APP_KEY!
                    },
                    body: JSON.stringify({
                        customerId: connection.finicityCustomerId
                    })
                });

                if (!response.ok) {
                    throw new Error(`Failed to refresh accounts for connection ${connection.id}`);
                }

                const data = await response.json();

                // Update accounts in Firestore
                for (const account of data.accounts) {
                    await addAccount({
                        name: account.name,
                        type: account.type,
                        balance: account.balance,
                        availableToBudget: account.detail?.availableBalance || 0,
                        color: connection.institutionId ?
                            `#${(parseInt(connection.institutionId) * 123456).toString(16).slice(0, 6)}` :
                            '#' + Math.floor(Math.random() * 16777215).toString(16),
                        accessToken: connection.accessToken,
                        externalId: account.id,
                        institution: connection.institutionId,
                        finicityCustomerId: connection.finicityCustomerId,
                        createdBy: auth.currentUser?.uid
                    });
                }

                // Update connection last sync time
                await updateBankConnection({
                    ...connection,
                    lastSyncTime: new Date()
                });
            });

            await Promise.all(promises);
            dispatch({ type: 'SET_LAST_SYNC', payload: new Date() });
            toast.success('All accounts refreshed successfully');
        } catch (error) {
            console.error('Error refreshing accounts:', error);
            toast.error('Failed to refresh some accounts');
            throw error;
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [state.activeConnections, addAccount, budgetState.currentBudget?.id]);

    const startLinking = useCallback(async () => {
        dispatch({ type: 'SET_LINKING', payload: true });
        try {
            const connectUrl = await getLinkToken();
            if (!connectUrl) throw new Error('Failed to get connect URL');

            const width = 600;
            const height = 600;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;

            const popup = window.open(
                connectUrl,
                'Connect Bank',
                `width=${width},height=${height},top=${top},left=${left}`
            );

            if (!popup) throw new Error('Popup blocked');

            const handleMessage = async (event: MessageEvent) => {
                if (event.origin === process.env.NEXT_PUBLIC_MASTERCARD_CONNECT_URL) {
                    if (event.data.status === 'success') {
                        await handleBankCallback(event.data.institutionLoginId);
                    } else if (event.data.status === 'exit') {
                        dispatch({ type: 'SET_LINKING', payload: false });
                    }
                    // Clean up event listener after receiving response
                    window.removeEventListener('message', handleMessage);
                }
            };

            window.addEventListener('message', handleMessage);
        } catch (error) {
            console.error('Error starting bank link:', error);
            toast.error('Failed to start bank connection');
            dispatch({ type: 'SET_LINKING', payload: false });
            throw error;
        }
    }, [getLinkToken, handleBankCallback]);

    const disconnectBank = useCallback(async (connectionId: string) => {
        try {
            const connection = state.activeConnections.find(c => c.id === connectionId);
            if (!connection) {
                throw new Error('Connection not found');
            }

            const connectionRef = doc(db, 'bank_connections', connectionId);
            await updateDoc(connectionRef, {
                status: 'disconnected',
                updatedAt: new Date()
            });

            dispatch({
                type: 'UPDATE_CONNECTION',
                payload: {
                    ...connection,
                    status: 'disconnected',
                    updatedAt: new Date()
                }
            });

            toast.success('Bank disconnected successfully');
        } catch (error) {
            console.error('Error disconnecting bank:', error);
            toast.error('Failed to disconnect bank');
            throw error;
        }
    }, [state.activeConnections]);

    return (
        <BankLinkingContext.Provider
            value={{
                startLinking,
                getLinkToken,
                handleBankCallback,
                refreshAccounts,
                disconnectBank,
                isLinking: state.isLinking,
                error: state.error,
                lastSyncTime: state.lastSyncTime,
                activeConnections: state.activeConnections,
                loading: state.loading
            }}
        >
            {children}
        </BankLinkingContext.Provider>
    );
}

export function useBankLinking() {
    const context = useContext(BankLinkingContext);
    if (context === undefined) {
        throw new Error('useBankLinking must be used within a BankLinkingProvider');
    }
    return context;
}

export default BankLinkingProvider;